import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {ReturnType<typeof setTimeout>[]} */
  #installmentTimers = [];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.addEventListener(DialogOpenEvent.eventName, this.#onDialogOpen);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.removeEventListener(DialogOpenEvent.eventName, this.#onDialogOpen);
    this.#clearInstallmentTimers();
  }

  #handleCartAdd = (event) => {
    if (!this.hasAttribute('auto-open')) return;

    // In-drawer upsell updates already keep the dialog open. Re-running showDialog
    // after a section remorph can leave the drawer stuck behind page content.
    const source = event?.detail?.data?.source;
    if (source === 'fluevog-cart-gift-card-upsell') return;

    this.showDialog();
  };

  #handleCartUpdate = () => {
    // Drawer HTML is remorphed on cart updates; re-check installments after paint.
    requestAnimationFrame(() => this.#syncInstallmentsMessaging());
  };

  #onDialogOpen = () => {
    this.#updateStickyState();
    this.#syncInstallmentsMessaging();
    this.#bindInstallmentsClose();
  };

  open() {
    this.showDialog();
    this.#bindInstallmentsClose();
  }

  close() {
    this.closeDialog();
  }

  #clearInstallmentTimers() {
    for (const timer of this.#installmentTimers) clearTimeout(timer);
    this.#installmentTimers = [];
  }

  /**
   * Keep custom Afterpay visible until Shopify payment-terms has real content.
   * After drawer morphs, the empty web component exists immediately and used to
   * hide the fallback before Shopify finished hydrating on mobile.
   */
  #syncInstallmentsMessaging = () => {
    const { dialog } = /** @type {Refs} */ (this.refs);
    const host = dialog?.querySelector('.fv-cart-installments');
    if (!(host instanceof HTMLElement)) return;

    const terms = host.querySelector('shopify-payment-terms');
    if (!(terms instanceof HTMLElement)) {
      host.classList.remove('fv-cart-installments--shopify-ready');
      return;
    }

    const markReady = () => {
      if (!this.isConnected || !host.isConnected) return;
      const shadow = terms.shadowRoot;
      const hasContent = Boolean(
        terms.childElementCount ||
          shadow?.querySelector('*') ||
          shadow?.textContent?.trim()
      );
      host.classList.toggle('fv-cart-installments--shopify-ready', hasContent);
    };

    this.#clearInstallmentTimers();
    markReady();
    this.#installmentTimers.push(
      setTimeout(markReady, 0),
      setTimeout(markReady, 250),
      setTimeout(markReady, 1000)
    );

    customElements.whenDefined('shopify-payment-terms').then(() => {
      markReady();
      requestAnimationFrame(markReady);
    });
  };

  #bindInstallmentsClose() {
    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const { dialog } = /** @type {Refs} */ (this.refs);
      const installmentsContent = dialog?.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
